import React, {useEffect, useState} from "react";
import { colors, fonts, glyphs } from "../../Styles";
import { styled } from "styletron-react";

// #region | CheckStyles
/* eslint-disable no-unused-vars */
export enum CheckStyles {
  transparent = "transparent",
  grey = "grey",
  blue = "blue",
  greyPrimary = "greyPrimary",
  greyTertiary = "greyTertiary"
}
/* eslint-enable no-unused-vars */

const createCheckStyle = (checkStyle?: CheckStyles | string, focus_view?:boolean ) => {
  let colorStyle = ""
  let borderStyle = ""
  let borderRadius = ""
  let size = ""
  let textStyle
  let checkMarkColor: colors

  switch (checkStyle) {
    case CheckStyles.transparent:
      colorStyle = colors.transparent
      borderStyle = "2px solid "+colors.greyTertiary
      borderRadius = "5px"
      size = "22px"
      textStyle = {color: "none", ...fonts.fontSize3}
      checkMarkColor = colors.systemGreen
      break
    case CheckStyles.grey:
      colorStyle = colors.transparent
      borderStyle = "2px solid "+colors.greyLightSecondary
      borderRadius = "5px"
      size = focus_view ? "30px" : "22px"
      textStyle = {color: colors.greyDarkPrimary, ...(focus_view ? fonts.fontSize3 : fonts.fontSize4)}
      checkMarkColor = colors.systemGreen
      break
    case CheckStyles.greyPrimary:
      colorStyle = colors.greySecondary
      borderStyle = "none"
      borderRadius = "5px"
      size = focus_view ? "30px" : "22px"
      textStyle = {color: colors.greyLightTertiary, ...fonts.fontSize4}
      checkMarkColor = colors.greyLightTertiary
      break
    case CheckStyles.greyTertiary:
      colorStyle = colors.greyTertiary
      borderStyle = "none"
      borderRadius = "8px"
      size = "25px"
      textStyle = {color: colors.greyDarkPrimary, ...(focus_view ? fonts.fontSize3 : fonts.fontSize4)}
      checkMarkColor = colors.systemGreen
      break
    case CheckStyles.blue:
      default:
        colorStyle = colors.blueDarkPrimary
        borderStyle = "2px solid "+colors.blueDarkPrimary
        borderRadius = "5px"
        size = "22px"
        textStyle = {color: colors.greyLightTertiary, ...fonts.fontSize4}
        checkMarkColor = colors.greyLightTertiary
      break
  }

  return {colorStyle, borderStyle, borderRadius, size, textStyle, checkMarkColor}
}
// #endregion

// #region | Styled Input Type Checkbox
interface StyledInputProps {
  size?: string
  disabled?: boolean
}

export const StyledCheckBox = styled<"input", StyledInputProps>("input", (props) => ({
  width: props.size,
  height: props.size,
  cursor: props.disabled ? "not-allowed" : 'pointer',
  pointerEvents: 'auto',
  appearance: 'none',
  margin: "0px",
  zIndex: 3,
  position: "absolute", top: "50%", left: "50%", transform: "translate(-50%, -50%)",
}));

// #endregion
interface Check_Props {
  text?: string
  isChecked?: boolean;
  disabled?:boolean;
  onChange?: (e:boolean) => void;
  checkStyle?: CheckStyles
  focus_view?: boolean
}
/**
 * CheckBox
 * @param text - The text displayed next to the checkbox.
 * @param isChecked - Indicates whether the checkbox is checked.
 * @param disabled - Disables the checkbox if `true`.
 * @param onChange - Callback function triggered when the value changes.
 * @param checkStyle - Defines the style of the checkbox (e.g., color and size).
 * @returns A styled checkbox with optional text.
 */
export default function CheckBox({ text, onChange, disabled, isChecked, checkStyle, focus_view }:Check_Props) {
  const [checked, setChecked] = useState(isChecked || false);
  const { colorStyle, borderStyle, borderRadius, size, checkMarkColor, textStyle } = createCheckStyle(checkStyle, focus_view);

  useEffect(() => {
    setChecked(isChecked || false);
  }, [isChecked]);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newChecked = e.target.checked;
    setChecked(newChecked);
    if (onChange) {
      onChange(newChecked);
    }
  };

  const blueStyles = { backgroundColor: colors.blueDarkSecondary, padding: "6px 10px", borderRadius: "10px" }
  const textStyles = { display: "flex", flexDirection: "row" as const, alignItems: "center", justifyContent: "start", gap: "10px" }
  const _root = text ? checkStyle === CheckStyles.blue ? { ...textStyles, ...blueStyles } : textStyles : {};
    
  return (
    <div style={{..._root, filter: disabled ? "opacity(0.5)" : "opacity(1)", cursor: disabled ? "not-allowed" : "default"}}> 
      <div style={{position: "relative", width: size, height: size, boxSizing: "border-box", zIndex: "1", backgroundColor: colorStyle, border: borderStyle, borderRadius: borderRadius }}>
        <StyledCheckBox
          type="checkbox"
          checked={checked}
          onChange={handleChange}
          disabled={disabled} 
          size={size}
        />
        {!checked 
          ? <></>
          : <span style={{ zIndex: "2", position: "absolute", top: "50%", left: "50%", transform: "translate(-50%, -50%)", width: focus_view ? "25px" : "20px", height: focus_view ? "25px" : "20px"}}>
              <glyphs.Check fillColor={checkMarkColor} size={focus_view ? "25px" : "20px"}/>
            </span>
          }
      </div>
      <p style={{...fonts.paragraph, ...textStyle, pointerEvents: "none"}}>{text}</p>
    </div>
  );
}